import { gestureReducer, type GestureEvent } from '../audio/gesture.ts';
import type { Register } from '../profile/register.ts';

/**
 * The talk presses one conversation saw (kid engagement PRD, F0). A press
 * the server never hears from is the one loss only the client can count:
 * with it, "never sent" (a microphone, a press too short) reads apart from
 * "sent and not heard".
 */

/**
 * Whether a gesture opens a capture: the press. A kid taps to start and taps
 * to stop, both `down`, so counting every `down` counted each utterance
 * twice in the register the count exists for.
 */
export function opensCapture(register: Register, capturing: boolean, event: GestureEvent): boolean {
  return !capturing && gestureReducer(register, { capturing }, event).capturing;
}

type PressStorage = { getItem(key: string): string | null; setItem(key: string, value: string): void };

/**
 * A running count kept in `sessionStorage` per session, so a reload carries
 * on from it rather than starting again: the sessions a parent reloads are
 * the ones whose microphone "doesn't work". Storage that throws (a private
 * window) costs the reload, never the count.
 */
export function createPressCounter(
  sessionId: string,
  storage: PressStorage | null,
): { press(): void; total(): number } {
  const key = `tutor.presses.${sessionId}`;
  let count = 0;
  try {
    count = Number.parseInt(storage?.getItem(key) ?? '0', 10) || 0;
  } catch {
    count = 0;
  }
  return {
    press() {
      count += 1;
      try {
        storage?.setItem(key, String(count));
      } catch {
        // Counted in memory still.
      }
    },
    total: () => count,
  };
}

/**
 * The report, as a plain `keepalive` request rather than a Relay mutation:
 * it must outlive the page when the tab closes or the app is swiped away,
 * which is when the count matters most. Sent as often as the page hides or
 * the session finishes; the server keeps the larger, so a repeat costs
 * nothing.
 */
export function pressesRequest(sessionId: string, presses: number): RequestInit {
  return {
    method: 'POST',
    keepalive: true,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      query:
        // oxlint-disable-next-line tutor/no-english -- a GraphQL document, not copy
        'mutation($sessionId: ID!, $presses: Int!) { recordTalkPresses(sessionId: $sessionId, presses: $presses) }',
      variables: { sessionId, presses },
    }),
  };
}
